"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Plus, Settings, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/lib/localization";
import { scaledPx } from "../lib/utils";

// Sizes are unitless at the design resolution (1920×1080); scaledPx() turns
// them into responsive CSS values via --scene-font-scale, so the chrome
// shrinks with the scene on small screens instead of overflowing it.
const SETTINGS_BUTTON_SIZE = 52;
const SETTINGS_ICON_SIZE = 26;
const SETTINGS_OFFSET = 72;
const SETTINGS_PADDING = 18;
const REMOVE_BUTTON_SIZE = 38;
const REMOVE_ICON_SIZE = 20;
const REMOVE_OFFSET = 14;

/**
 * Single hover-revealed entry point for a custom block's settings.
 * Sits just outside the block's start edge; opens a popover with
 * an optional custom section (block-specific controls), an add-item
 * action, and a destructive delete action.
 */
export function BlockSettingsButton({
    customSection,
    onAddItem,
    addItemLabelKey,
    onDelete,
}: {
    customSection?: React.ReactNode;
    onAddItem: () => void;
    /** Localization key for the add-item label (e.g. "lomda.scene-block.add-card"). */
    addItemLabelKey: string;
    onDelete: () => void;
}) {
    const { dir, locale } = useLocale();
    const [open, setOpen] = React.useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            {/*
              Span wrapper bridges the visual gap between the grid and the
              gear button. The button itself is offset visually via the
              wrapper's inline-end padding, but the wrapper covers the
              entire slot — so moving the cursor from the grid to the gear
              never leaves the NodeViewWrapper's :hover area, and the
              button doesn't flicker on the way over.
            */}
            <span
                contentEditable={false}
                className={cn(
                    "absolute z-30 inline-flex items-start",
                    "opacity-0 group-hover/scene-block:opacity-100 focus-within:opacity-100",
                    "[.ProseMirror-selectednode_&]:opacity-100",
                    "transition-opacity duration-150",
                    open && "opacity-100"
                )}
                // Span fills the entire vertical extent of the block so
                // approaching diagonally (from below or above the gear's
                // own height) still keeps the cursor inside the wrapper's
                // hover region. align-items: start pins the visible
                // button to the top edge.
                style={{
                    top: 0,
                    bottom: 0,
                    insetInlineStart: `calc(-1 * ${scaledPx(SETTINGS_OFFSET)})`,
                    paddingInlineEnd: scaledPx(SETTINGS_PADDING),
                }}
            >
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        aria-label={t(
                            "lomda.scene-block.settings.aria",
                            locale
                        )}
                        data-block-control="settings"
                        // Stop pointerdown too: the canvas gesture layer listens
                        // on pointer events, and letting it through would enter
                        // text-edit mode on click — the re-render races Radix's
                        // open and eats the first click.
                        onPointerDown={(e) => {
                            e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        className={cn(
                            "inline-flex items-center justify-center rounded-full",
                            "bg-background/90 backdrop-blur-sm border border-border",
                            "text-foreground hover:bg-muted hover:border-border",
                            "transition-[background-color,border-color] duration-150",
                            "!cursor-pointer pointer-events-auto",
                            open && "bg-muted"
                        )}
                        style={{
                            width: scaledPx(SETTINGS_BUTTON_SIZE),
                            height: scaledPx(SETTINGS_BUTTON_SIZE),
                        }}
                    >
                        <Settings
                            style={{
                                width: scaledPx(SETTINGS_ICON_SIZE),
                                height: scaledPx(SETTINGS_ICON_SIZE),
                            }}
                        />
                    </button>
                </PopoverTrigger>
            </span>
            <PopoverContent
                dir={dir}
                align="start"
                side="left"
                sideOffset={6}
                className="w-72 p-3"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col gap-3">
                    {customSection}

                    {customSection ? (
                        <div className="h-px bg-border -mx-1" />
                    ) : null}

                    <div className="flex items-stretch gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="h-8 flex-1 justify-center gap-1.5 text-xs"
                            onClick={(e) => {
                                e.preventDefault();
                                onAddItem();
                            }}
                        >
                            <Plus className="size-3.5" />
                            {t(addItemLabelKey, locale)}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 flex-1 justify-center gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border border-destructive/20"
                            onClick={(e) => {
                                e.preventDefault();
                                setOpen(false);
                                onDelete();
                            }}
                        >
                            <Trash2 className="size-3.5" />
                            {t("lomda.scene-block.delete", locale)}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

/** Per-item × button — appears on hover over a single item inside a block. */
export function RemoveItemButton({ onClick }: { onClick: () => void }) {
    const { locale } = useLocale();
    return (
        <button
            type="button"
            aria-label={t("lomda.scene-block.remove", locale)}
            contentEditable={false}
            data-block-control="remove"
            onPointerDown={(e) => {
                e.stopPropagation();
            }}
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
            }}
            className={cn(
                "absolute z-20 inline-flex items-center justify-center rounded-full",
                "bg-background/90 backdrop-blur-sm border border-border",
                "text-foreground hover:bg-muted hover:border-border",
                "transition-[opacity,background-color,border-color] duration-150",
                "opacity-0 group-hover/scene-block-item:opacity-100",
                "!cursor-pointer pointer-events-auto"
            )}
            style={{
                top: `calc(-1 * ${scaledPx(REMOVE_OFFSET)})`,
                insetInlineEnd: `calc(-1 * ${scaledPx(REMOVE_OFFSET)})`,
                width: scaledPx(REMOVE_BUTTON_SIZE),
                height: scaledPx(REMOVE_BUTTON_SIZE),
            }}
        >
            <X
                style={{
                    width: scaledPx(REMOVE_ICON_SIZE),
                    height: scaledPx(REMOVE_ICON_SIZE),
                }}
            />
        </button>
    );
}
